import MuiChip from '@mui/material/Chip';
import { alpha, type Theme } from '@mui/material/styles';
import type { ReactElement } from 'react';

export interface ChipProps {
  label: string;
  color?: 'default' | 'success' | 'error' | 'warning' | 'info';
  variant?: 'filled' | 'outlined';
  size?: 'small' | 'medium';
  // `status` (default) reports a state and is read as a field of its row. `mark` labels the thing
  // it sits beside — a capsule with a smaller, roomier label in the deeper tone of its colour, so
  // it reads as applied to the heading next to it and never competes with it. `counter` quantifies
  // it: a dense capsule holding a number, short enough to ride on the line of a label.
  kind?: 'status' | 'mark' | 'counter';
  // A leading icon element (e.g. from the façade's icons).
  icon?: ReactElement;
  // Makes the chip an action: MUI renders it clickable (role="button", focusable).
  onClick?: () => void;
  // Raises the label from the chip's semibold default to full bold, for a chip that has to be read
  // BEFORE the text around it rather than alongside it. Same vocabulary as `Text`'s `bold`.
  bold?: boolean;
  // The label's type size. `default` inherits MUI's chip label (13px at `size="small"`); `small`
  // takes the facade's compact 11px — for a chip that states a fact ABOUT its row and must sit
  // under the row's own text without competing with it. Independent of `size`, which is the
  // capsule's height, and applied after `kind` so asking explicitly wins.
  labelSize?: 'default' | 'small';
}

// Soft/tonal fill for the filled chip: a pale tint of the semantic colour as the
// background with the colour's dark shade as the bold label, so the status reads as a
// quiet pill instead of a solid block. The neutral case degrades to a grey tint.
// The `mark` treatment. The radius is large enough to clamp to a capsule at any chip height, so no
// size needs its own number. The label drops below the chip's default because a mark sits next to
// a heading and must not read as loud as it, and the extra side padding is what keeps the shorter
// text from touching the capsule's curve. The deeper tone (`dark`) rather than `main` is what makes
// an all-caps label at 11px still clear contrast against the surface it floats on.
// The facade's ONE compact label size. Shared by the mark and counter kinds and by `labelSize`, so
// «smaller chip text» means the same thing everywhere instead of a number per caller.
const COMPACT_LABEL_FONT_SIZE = '11px';
// Inset from the capsule to what it holds: the icon on the left, the text on the right.
const MARK_INSET_PX = 12;
// Distance from the icon to the text — its own quantity, and deliberately tighter than the inset
// so the glyph reads as part of the words rather than as a second element in a row.
const MARK_ICON_GAP_PX = 4;
const PILL_RADIUS = '9999px';
// The two label weights a chip can carry: its own semibold, and full bold when the caller asks.
const LABEL_WEIGHT = 600;
const LABEL_WEIGHT_BOLD = 700;

function markSx(color: NonNullable<ChipProps['color']>) {
  return (theme: Theme) => ({
    borderRadius: PILL_RADIUS,
    fontSize: COMPACT_LABEL_FONT_SIZE,
    color: color === 'default' ? theme.palette.text.primary : theme.palette[color].dark,
    // MUI colours the icon from its own rule, so the chip's colour does not reach it on its own.
    // Its 4px inset is for a chip that leads with a status glyph; here the icon IS the mark's left
    // edge, so it takes the same inset the label has on the right and the capsule stays even.
    //
    // The icon→text gap is NOT the right margin: the label keeps its own left padding, so the two
    // add up. The margin is solved for the gap we want, which is why it comes out negative.
    '& .MuiChip-icon': {
      color: 'inherit',
      fontSize: '14px',
      marginLeft: `${String(MARK_INSET_PX)}px`,
      marginRight: `${String(MARK_ICON_GAP_PX - MARK_INSET_PX)}px`,
    },
    '& .MuiChip-label': {
      paddingLeft: `${String(MARK_INSET_PX)}px`,
      paddingRight: `${String(MARK_INSET_PX)}px`,
    },
  });
}

// The `counter` treatment. Solved for a capsule that rides ON the line of the label it counts for,
// so it is shorter than any chip size MUI offers and its inset is half a mark's — two or three
// glyphs in a 12px-inset capsule would be mostly air. The colour lives in the fill and the rule
// around it; the digits stay ink, because a quantity is not a state and must not be read as one.
const COUNTER_HEIGHT_PX = 16;
const COUNTER_INSET_PX = 6;

function counterSx(color: NonNullable<ChipProps['color']>) {
  return (theme: Theme) => ({
    height: `${String(COUNTER_HEIGHT_PX)}px`,
    borderRadius: PILL_RADIUS,
    fontSize: COMPACT_LABEL_FONT_SIZE,
    // The capsule is shorter than the line the inherited body typography would give the label
    // (11px × 1.5 ≈ 17px), which would push the digits against its top edge. Set to the glyphs.
    lineHeight: 1,
    color: theme.palette.text.primary,
    // Written out even on the filled variant: at this size the tint alone is too faint to hold an
    // edge against the card, and the rule is what makes the capsule a capsule.
    border: `1px solid ${color === 'default' ? theme.palette.divider : theme.palette[color].main}`,
    '& .MuiChip-label': {
      paddingLeft: `${String(COUNTER_INSET_PX)}px`,
      paddingRight: `${String(COUNTER_INSET_PX)}px`,
    },
  });
}

function softFill(color: NonNullable<ChipProps['color']>) {
  return (theme: Theme) => {
    const { backgroundColor, textColor } =
      color === 'default'
        ? {
            backgroundColor: alpha(theme.palette.text.primary, 0.08),
            textColor: theme.palette.text.primary,
          }
        : {
            backgroundColor: alpha(theme.palette[color].main, 0.16),
            textColor: theme.palette[color].dark,
          };
    return { backgroundColor, color: textColor, fontWeight: LABEL_WEIGHT, borderRadius: 2 };
  };
}

// Status chip behind the facade. The label always carries the state in text, so the
// meaning never rests on colour alone (a11y). Callers map a domain status to `color`;
// the filled (default) variant renders the soft tonal style, `outlined` stays plain.
// With `onClick` it doubles as a compact pill action (e.g. the Delivery ID copy).
export function Chip({
  label,
  color = 'default',
  variant = 'filled',
  size = 'small',
  kind = 'status',
  icon,
  onClick,
  bold = false,
  labelSize = 'default',
}: ChipProps) {
  // The kind is applied after the variant's own style, so a mark's or a counter's capsule and
  // typography win over the 8px rectangle both variants otherwise share.
  const kindSx =
    kind === 'mark' ? [markSx(color)] : kind === 'counter' ? [counterSx(color)] : [];
  // Last in the sx array so they win over the weight and size the variant and the kind set.
  const weightSx = bold ? [{ fontWeight: LABEL_WEIGHT_BOLD }] : [];
  const labelSizeSx = labelSize === 'small' ? [{ fontSize: COMPACT_LABEL_FONT_SIZE }] : [];
  return (
    <MuiChip
      label={label}
      color={color}
      variant={variant}
      size={size}
      // One chip shape and typography across variants: outlined shares the soft fill's
      // rounded rectangle (8px, not MUI's full pill) and its semibold label.
      {...(variant === 'filled'
        ? { sx: [softFill(color), ...kindSx, ...weightSx, ...labelSizeSx] }
        : {
            sx: [
              { borderRadius: 2, fontWeight: LABEL_WEIGHT },
              ...kindSx,
              ...weightSx,
              ...labelSizeSx,
            ],
          })}
      {...(icon ? { icon } : {})}
      {...(onClick ? { onClick } : {})}
    />
  );
}
